import React, { useEffect, useState } from 'react';
import ReportNote from '../components/ReportNote';
import { monthRange } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const PRIORITY_COLOR = {
  Blocker: '#dc2626',
  High:    '#ea580c',
  Medium:  '#2563eb',
  Minor:   '#16a34a',
  Low:     '#6b7280',
};

function pctColor(pct) {
  if (pct >= 90) return '#16a34a';
  if (pct >= 70) return '#d97706';
  return '#dc2626';
}

function fmtH(h) {
  if (h >= 48) return `${Math.round(h / 24)}j`;
  if (h >= 24) return `${(h / 24).toFixed(1)}j`;
  return `${Math.round(h)}h`;
}

export default function SLAReport() {
  const [offset, setOffset]     = useState(0);
  const [fromDate, setFromDate] = useState(monthRange(0).from);
  const [toDate, setToDate]     = useState(monthRange(0).to);
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('closed');
  const [jiraUrl, setJiraUrl]   = useState(localStorage.getItem('jiraUrl') || '');

  useEffect(() => {
    fetch(`${API}/api/settings`).then(r => r.json()).then(d => {
      if (d.jira_url) { setJiraUrl(d.jira_url); localStorage.setItem('jiraUrl', d.jira_url); }
    }).catch(() => {});
  }, []);

  const load = (f, t) => {
    setLoading(true);
    fetch(`${API}/api/reports/sla?from_date=${f}&to_date=${t}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    const r = monthRange(offset);
    setFromDate(r.from); setToDate(r.to);
    load(r.from, r.to);
  }, [offset]);

  const applyCustom = () => load(fromDate, toDate);

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ['Priorité', 'Cible', 'Moy. réelle (h)', 'Tickets', 'Respectés', '%'],
      ...data.by_priority.map(p => [p.priority, p.target_display, p.avg_h, p.count, p.met, p.pct + '%']),
      [],
      ['--- Tickets hors SLA (fermés) ---'],
      ['Ticket', 'Résumé', 'Priorité', 'Assigné', 'Client', 'Durée (h)', 'Dépassement (h)', 'Statut'],
      ...data.breached.map(t => [t.issue_key, t.summary, t.priority, t.assignee, t.customer, t.actual_h, t.over_h, t.status]),
      [],
      ['--- Tickets hors SLA (ouverts) ---'],
      ['Ticket', 'Résumé', 'Priorité', 'Assigné', 'Client', 'Durée (h)', 'Dépassement (h)', 'Statut'],
      ...data.open_breached.map(t => [t.issue_key, t.summary, t.priority, t.assignee, t.customer, t.actual_h, t.over_h, t.status]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `SLA_${fromDate}_${toDate}.csv`;
    a.click();
  };

  const TicketKey = ({ k }) => {
    if (jiraUrl) return (
      <a href={`${jiraUrl}/browse/${k}`} target="_blank" rel="noreferrer"
         style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' }}>
        {k}
      </a>
    );
    return <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{k}</span>;
  };

  const PriorityBadge = ({ p }) => (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      background: (PRIORITY_COLOR[p] || '#888') + '22',
      color: PRIORITY_COLOR[p] || '#888', fontWeight: 700, fontSize: 12,
    }}>{p}</span>
  );

  const breachList = tab === 'closed' ? (data?.breached || []) : (data?.open_breached || []);

  return (
    <div>
      <ReportNote what="SLA performance: response and resolution against target." from={fromDate} to={toDate} />
      {/* Controls */}
      <div className="no-print" style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button onClick={() => setOffset(o => o - 1)} className="btn btn-secondary" style={{ padding:'6px 12px' }}>‹</button>
          <span style={{ fontWeight:700, fontSize:15, minWidth:140, textAlign:'center' }}>{monthRange(offset).label}</span>
          <button onClick={() => setOffset(o => o + 1)} className="btn btn-secondary" style={{ padding:'6px 12px' }}>›</button>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, paddingLeft:8, borderLeft:'1px solid var(--border)' }}>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="input" style={{ width:140 }} />
          <span style={{ color:'var(--text-2)' }}>→</span>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="input" style={{ width:140 }} />
          <button onClick={applyCustom} className="btn btn-primary" style={{ padding:'6px 14px' }}>Apply</button>
        </div>
        <div style={{ display:'flex', gap:8, marginLeft:'auto' }}>
          <button onClick={exportCSV} className="btn btn-secondary">↓ CSV</button>
          <button onClick={() => window.print()} className="btn btn-secondary">🖨 Print / PDF</button>
        </div>
      </div>

      {loading && <div className="loading">Chargement SLA…</div>}

      {!loading && data && (
        <>
          {/* KPI cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:24 }}>
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text-2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>Conformité globale</div>
              <div style={{ fontSize:38, fontWeight:800, color: data.overall_pct != null ? pctColor(data.overall_pct) : 'var(--text-2)' }}>
                {data.overall_pct != null ? `${data.overall_pct}%` : '—'}
              </div>
              <div style={{ fontSize:13, color:'var(--text-2)', marginTop:4 }}>{data.total_met} / {data.total_measured} tickets respectés</div>
            </div>
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text-2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>Tickets mesurés</div>
              <div style={{ fontSize:38, fontWeight:800, color:'var(--text)' }}>{data.total_measured}</div>
              <div style={{ fontSize:13, color:'var(--text-2)', marginTop:4 }}>tickets fermés sur la période</div>
            </div>
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text-2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>Hors SLA (fermés)</div>
              <div style={{ fontSize:38, fontWeight:800, color: data.breached.length > 0 ? '#dc2626' : '#16a34a' }}>{data.breached.length}</div>
              <div style={{ fontSize:13, color:'var(--text-2)', marginTop:4 }}>
                + {data.open_breached.length} ouverts en dépassement
              </div>
            </div>
          </div>

          {/* By priority */}
          <div className="card" style={{ marginBottom:24 }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', fontWeight:700, fontSize:15 }}>
              Conformité par priorité
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid var(--border)' }}>
                    {['Priorité','Cible','Moy. réelle','Tickets','Respectés','%'].map(h => (
                      <th key={h} style={{ padding:'10px 16px', textAlign: h === 'Priorité' ? 'left' : 'right', fontSize:11, fontWeight:600, color:'var(--text-2)', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                    ))}
                    <th style={{ padding:'10px 16px' }} />
                  </tr>
                </thead>
                <tbody>
                  {data.by_priority.map(p => (
                    <tr key={p.priority} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'12px 16px' }}><PriorityBadge p={p.priority} /></td>
                      <td style={{ padding:'12px 16px', textAlign:'right', color:'var(--text-2)' }}>{p.target_display}</td>
                      <td style={{ padding:'12px 16px', textAlign:'right', fontWeight:600, color: p.avg_h <= p.target_h ? '#16a34a' : '#dc2626' }}>{p.avg_display}</td>
                      <td style={{ padding:'12px 16px', textAlign:'right' }}>{p.count}</td>
                      <td style={{ padding:'12px 16px', textAlign:'right' }}>{p.met}</td>
                      <td style={{ padding:'12px 16px', textAlign:'right' }}>
                        <span style={{ fontWeight:700, color: pctColor(p.pct) }}>{p.pct}%</span>
                      </td>
                      <td style={{ padding:'12px 16px', width:120 }}>
                        <div style={{ height:8, borderRadius:4, background:'var(--bg-2)', overflow:'hidden' }}>
                          <div style={{ height:'100%', width:`${p.pct}%`, background: pctColor(p.pct), borderRadius:4, transition:'width .3s' }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {data.by_priority.length === 0 && (
                    <tr><td colSpan={7} style={{ padding:24, textAlign:'center', color:'var(--text-2)' }}>Aucun ticket sur cette période</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Breach list */}
          <div className="card">
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', gap:12, alignItems:'center' }}>
              <span style={{ fontWeight:700, fontSize:15 }}>Tickets hors SLA</span>
              {['closed','open'].map(t => (
                <button key={t} onClick={() => setTab(t)} style={{
                  padding:'4px 14px', borderRadius:20, border:'none', cursor:'pointer',
                  fontWeight:600, fontSize:13,
                  background: tab === t ? 'var(--accent)' : 'var(--bg-2)',
                  color: tab === t ? '#fff' : 'var(--text-2)',
                }}>
                  {t === 'closed' ? `Fermés (${data.breached.length})` : `Ouverts (${data.open_breached.length})`}
                </button>
              ))}
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid var(--border)' }}>
                    {['Ticket','Résumé','Priorité','Assigné','Client','Durée','Dépassement','Statut'].map(h => (
                      <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--text-2)', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {breachList.map(t => (
                    <tr key={t.issue_key} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'10px 16px', whiteSpace:'nowrap' }}><TicketKey k={t.issue_key} /></td>
                      <td style={{ padding:'10px 16px', maxWidth:260, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'var(--text-2)', fontSize:13 }} title={t.summary}>{t.summary}</td>
                      <td style={{ padding:'10px 16px', whiteSpace:'nowrap' }}><PriorityBadge p={t.priority} /></td>
                      <td style={{ padding:'10px 16px', fontSize:13, whiteSpace:'nowrap' }}>{(t.assignee || '').split(' ')[0]}</td>
                      <td style={{ padding:'10px 16px', fontSize:13, color:'var(--text-2)', whiteSpace:'nowrap' }}>{t.customer}</td>
                      <td style={{ padding:'10px 16px', fontWeight:600, whiteSpace:'nowrap' }}>{fmtH(t.actual_h)}</td>
                      <td style={{ padding:'10px 16px', color:'#dc2626', fontWeight:700, whiteSpace:'nowrap' }}>+{fmtH(t.over_h)}</td>
                      <td style={{ padding:'10px 16px', fontSize:13, color:'var(--text-2)', whiteSpace:'nowrap' }}>{t.status}</td>
                    </tr>
                  ))}
                  {breachList.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ padding:32, textAlign:'center', color:'#16a34a', fontWeight:600 }}>
                        ✓ Aucun ticket hors SLA sur cette période
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
